import { alpha, Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Assessment as AssessmentIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  C,
  FilterPill,
  HeaderMetric,
  MetricCell,
  RelativeStrengthCell,
  STATE_FILTERS,
  ScoreFilterGroup,
  SectionHeader,
  ShakeoutCell,
  candidateColumns,
  fmtNum,
  monoCellSx,
  sectionCardSx,
  sectorDisplayName,
  tableBodyCellSx,
  tableHeadCellSx,
  toneToColor,
} from './shared';

export default function WatchlistTable({
  t,
  lang,
  summary,
  candidates,
  scoreFloor,
  setScoreFloor,
  strongOnly,
  setStrongOnly,
  stateFilter,
  setStateFilter,
}) {
  return (
    <Paper elevation={0} sx={{ ...sectionCardSx, flex: 1.3 }}>
      <SectionHeader
        icon={<AssessmentIcon sx={{ fontSize: 16 }} />}
        title={t('bottomingFlow.table.title')}
        subtitle={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <HeaderMetric label={t('bottomingFlow.summary.visible')} value={candidates.length} color={C.acceleration} />
            <HeaderMetric label={t('bottomingFlow.summary.universe')} value={summary?.universe?.total || 0} color={C.slate} />
            <HeaderMetric label={t('bottomingFlow.summary.spotlight')} value={summary?.universe?.spotlight_count || 0} color={C.bottoming} />
          </Box>
        )}
        action={(
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
            <ScoreFilterGroup scoreFloor={scoreFloor} setScoreFloor={setScoreFloor} strongOnly={strongOnly} setStrongOnly={setStrongOnly} t={t} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, flexWrap: 'wrap', justifyContent: 'flex-end', px: 0.35, py: 0.25, borderRadius: 1, bgcolor: '#f8fafc' }}>
              {STATE_FILTERS.map((item) => (
                <FilterPill key={item.key} label={t(item.labelKey)} color={item.color} active={stateFilter === item.key} onClick={() => setStateFilter(item.key)} />
              ))}
            </Box>
          </Box>
        )}
      />
      <TableContainer sx={{ bgcolor: '#fff', overflowX: 'auto', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1220 }}>
          <TableHead>
            <TableRow>
              {candidateColumns.map((column) => (
                <TableCell key={column.labelKey} sx={{ ...tableHeadCellSx, ...column.sx }}>{t(column.labelKey)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.map((row, index) => {
              const state = row.display?.state || {};
              const stateColor = toneToColor(state.tone);
              return (
                <TableRow
                  key={`${row.ticker}-${row.score_date}`}
                  hover
                  sx={{
                    bgcolor: index % 2 === 0 ? '#fff' : '#fafbfc',
                    transition: 'background 0.15s',
                    '&:hover': { backgroundColor: alpha(stateColor, 0.04) },
                  }}
                >
                  <TableCell sx={{ ...tableBodyCellSx, width: 180 }}>
                    <Stack spacing={0.2}>
                      <Typography component={RouterLink} to={`/stocks/${row.ticker}`} sx={{ color: '#0f172a', fontWeight: 850, fontSize: '0.82rem', letterSpacing: '0.02em', textDecoration: 'none' }}>
                        {row.ticker}
                      </Typography>
                      <Typography sx={{ fontSize: '0.69rem', color: C.inkSoft, maxWidth: 160, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.35 }}>
                        {row.company_name || '-'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ ...tableBodyCellSx, width: 150 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.35, maxWidth: 132, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {sectorDisplayName(row, lang)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <Chip size="small" label={state.label_key ? t(state.label_key) : '-'} sx={{ height: 22, fontSize: '0.65rem', backgroundColor: alpha(stateColor, 0.08), color: stateColor, border: `1px solid ${alpha(stateColor, 0.18)}`, fontWeight: 700, borderRadius: 1 }} />
                  </TableCell>
                  <TableCell sx={{ ...tableBodyCellSx, textAlign: 'center' }}><MetricCell read={row.display?.metrics?.overall} t={t} /></TableCell>
                  <TableCell sx={{ ...tableBodyCellSx, textAlign: 'center' }}><MetricCell read={row.display?.metrics?.bottom} t={t} /></TableCell>
                  <TableCell sx={{ ...tableBodyCellSx, textAlign: 'center' }}><MetricCell read={row.display?.metrics?.supply} t={t} /></TableCell>
                  <TableCell sx={{ ...tableBodyCellSx, textAlign: 'center' }}><MetricCell read={row.display?.metrics?.flow} t={t} /></TableCell>
                  <TableCell sx={{ ...tableBodyCellSx, textAlign: 'center' }}><RelativeStrengthCell read={row.display?.metrics?.rs20} t={t} /></TableCell>
                  <TableCell sx={tableBodyCellSx}><ShakeoutCell read={row.display?.metrics?.shakeout} t={t} /></TableCell>
                  <TableCell sx={{ ...tableBodyCellSx, textAlign: 'right' }}><Typography sx={{ ...monoCellSx, color: '#0f172a', fontWeight: 800 }}>{fmtNum(row.price?.close, 1)}</Typography></TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <Stack spacing={0.15}>
                      <Typography sx={{ ...monoCellSx, color: '#475569' }}>{t('bottomingFlow.table.support')} {fmtNum(row.price?.base_support ?? row.price?.support_20d, 1)}</Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: C.inkSoft, fontFamily: 'monospace' }}>{t('bottomingFlow.table.resistance')} {fmtNum(row.price?.base_resistance ?? row.price?.resistance_20d, 1)}</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
